import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives';
import Select, { type FormatOptionLabelMeta } from '@databases/atlaskit/Select';
import { PageStatusColorPicker } from '@databases/color-picker';
import PageStatus from '@databases/field-value/pageStatus/PageStatus';
import { messages } from '@databases/intl/messages';
import { type Optional } from '@databases/types';
import { type ContentState } from '@databases/types/confluence';
import { getContentStateKey } from '@databases/utils/confluence/contentState';
import { DEFAULT_PAGE_STATUS_COLOR } from '@databases/utils/database/field-types/pageStatus';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import {
  baseSelectStyleConfig,
  customPlaceholderAndControlSpacingStyling,
} from '../popover-edit-common';

/** Max length which is allowed for a content state name */
const MAX_CONTENT_STATE_NAME_LENGTH = 20;

const ClearIndicator = (props: any) => {
  const {
    children = <CrossIcon label="" />,
    innerProps: { ref, ...innerProps },
  } = props;
  return (
    <Box {...innerProps} ref={ref}>
      {children}
    </Box>
  );
};

type Option = Optional<ContentState, 'id'>;

const getOptionValue = (op: Option) => getContentStateKey(op);
const getOptionLabel = (op: Option) => op.name;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
`;

export interface PageStatusSelectProps {
  customOptions: ReadonlyArray<Option>;
  spaceOptions: ReadonlyArray<Option>;
  value?: Option | undefined;
  onChange?: (value: Option | undefined) => void;
  isCreatable?: boolean;
  onCreate?: (option: Option) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  noOptionsMessage?: string;
  initialKey?: string;
}

const PageStatusSelect: FunctionComponent<PageStatusSelectProps> = ({
  customOptions,
  spaceOptions,
  value = Object.freeze([]) as never[],
  onChange,
  isCreatable = false,
  onCreate,
  onFocus,
  onBlur,
  autoFocus,
  placeholder,
  isLoading,
  noOptionsMessage,
  initialKey,
}) => {
  const [nextColor, setNextColor] = useState(DEFAULT_PAGE_STATUS_COLOR);

  const [inputValue, setInputValue] = useState(initialKey ?? '');

  const { formatMessage } = useIntl();
  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
  }, []);

  const createOption = useMemo(() => {
    if (isCreatable) {
      const trimmed = inputValue.trim();
      const trimmedLower = inputValue.trim().toLowerCase();
      if (
        trimmed &&
        trimmed.length <= MAX_CONTENT_STATE_NAME_LENGTH &&
        ![...customOptions, ...spaceOptions].some(
          (o) => o.name.toLowerCase() === trimmedLower
        )
      ) {
        return { name: trimmed, color: nextColor };
      }
    }
  }, [customOptions, inputValue, isCreatable, nextColor, spaceOptions]);

  const resolvedOptions = useMemo(
    () =>
      [
        {
          options: [...(createOption ? [createOption] : []), ...customOptions],
          label: formatMessage(
            messages['field-settings.type-page-status-picker-custom-text']
          ),
        },
        {
          options: spaceOptions,
          label: formatMessage(
            messages['field-settings.type-page-status-picker-suggested-text']
          ),
        },
      ].filter((g) => g.options.length > 0),
    [createOption, customOptions, formatMessage, spaceOptions]
  );

  const handleChange = useCallback(
    (val: ReadonlyArray<Option> | Option | null) => {
      const newValue: Option | undefined =
        (Array.isArray(val) ? val[0] : val) ?? undefined;
      if (onCreate && createOption && newValue === createOption) {
        onCreate(createOption);
      }

      onChange?.(newValue);
    },
    [createOption, onChange, onCreate]
  );

  const formatOptionLabel = useCallback(
    (option: Option, meta: FormatOptionLabelMeta<Option>) => {
      if (meta.context === 'menu' && option.id == null) {
        if (createOption && option === createOption) {
          return (
            <OptionContainer>
              {formatMessage(
                messages['field-settings.type-page-status-picker-create-text']
              )}
              &nbsp;
              <span style={{ flexGrow: 1 }}>
                <PageStatus
                  status={{
                    text: option.name,
                    color: option.color,
                  }}
                />
              </span>
              <PageStatusColorPicker
                value={option.color}
                onChange={(color) => {
                  setNextColor(color);
                }}
                stopPropagation
                size={24}
              />
            </OptionContainer>
          );
        } else {
          return (
            <OptionContainer>
              <PageStatus
                status={{
                  text: option.name,
                  color: option.color,
                }}
              />
              &nbsp;
              {formatMessage(messages['field-settings.type-page-status-picker-new-text'])}
            </OptionContainer>
          );
        }
      } else {
        return (
          <OptionContainer>
            <PageStatus
              status={{
                text: option.name,
                color: option.color,
              }}
            />
          </OptionContainer>
        );
      }
    },
    [createOption, formatMessage]
  );

  const resolvedNoOptionsMessage = useMemo(
    () => (noOptionsMessage ? () => noOptionsMessage : undefined),
    [noOptionsMessage]
  );

  return (
    <Select<Option>
      options={resolvedOptions}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      placeholder={
        placeholder ??
        (isCreatable
          ? formatMessage(messages['page-status-picker-popover.search-create-text'])
          : formatMessage(messages['select-dropdown.placeholder']))
      }
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      formatOptionLabel={formatOptionLabel}
      hideSelectedOptions={false}
      styles={{
        ...baseSelectStyleConfig,
        ...customPlaceholderAndControlSpacingStyling,
      }}
      isLoading={isLoading}
      noOptionsMessage={resolvedNoOptionsMessage}
      defaultMenuIsOpen={autoFocus}
      isClearable
      menuPortalTarget={null}
      components={{
        ClearIndicator,
      }}
      classNamePrefix="page-status-picker"
    />
  );
};

export default PageStatusSelect;
